import { useStore } from 'zustand';
import {
    createContext,
    useContext,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useAuth } from '@/entities/Auth/store';
import { connectChatDialogRealtime } from '@/entities/ChatDialog/api/chatDialogRealtime';
import { useChatDialogStore } from '@/entities/ChatDialog/model/store';
import type { ChatDialogConnectionState, ChatDialogRealtimeCallbacks } from '@/entities/ChatDialog/model/types';
import { RequestStatus } from '@/shared/model/requestStatus';

/** Поддерживает общую подписку на переписку для авторизованного сотрудника */
export const useChatDialogRealtime = (
    enabled: boolean,
    onEvent?: ChatDialogRealtimeCallbacks['onEvent'],
    store: typeof useChatDialogStore = useChatDialogStore,
    connect: typeof connectChatDialogRealtime = connectChatDialogRealtime,
) => {
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const refreshLive = useStore(store, (state) => state.refreshLive);
    const applyRealtime = useStore(store, (state) => state.applyRealtime);
    const setRealtimeEnabled = useStore(store, (state) => state.setRealtimeEnabled);
    const [attempt, setAttempt] = useState(0);
    const [pinsRevision, setPinsRevision] = useState(0);
    const [available, setAvailable] = useState(true);
    const [connection, setConnection] = useState<ChatDialogConnectionState>('connecting');
    const [error, setError] = useState<string | null>(null);
    const onEventRef = useRef(onEvent);

    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    useEffect(() => {
        if (!enabled) {
            setConnection('disabled');
            setError(null);

            return;
        }
        setAvailable(true);
        setConnection('connecting');
        let disposed = false;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;
        setError(null);

        /** Восстанавливает показанные данные после подключения */
        const restore = (): void => {
            if (disposed) return;
            if (retryTimer !== null) clearTimeout(retryTimer);
            retryTimer = null;

            void refreshLive()
                .then(() => {
                    if (disposed) return;
                    if (store.getState().refreshStatus !== RequestStatus.Error) {
                        setPinsRevision((value) => value + 1);

                        return;
                    }
                    retryTimer = setTimeout(restore, 5000);
                });
        };

        const stop = connect({
            onPinsChanged: () => {
                if (!disposed) setPinsRevision((value) => value + 1);
            },
            onAvailability: (value) => {
                if (!disposed) setAvailable(value);
            },
            onEvent: (event) => {
                if (disposed) return;
                onEventRef.current?.(event);
                applyRealtime(event);
            },
            onReady: restore,
            onState: (state) => {
                if (disposed) return;
                setConnection(state);

                if (state === 'connected') setRealtimeEnabled(true);
                if (state === 'disabled') setRealtimeEnabled(false);
                if (state === 'connected' || state === 'disabled') setError(null);
            },
            onError: (message) => {
                if (!disposed) setError(message);
            },
        });

        return () => {
            disposed = true;
            stop();

            if (retryTimer !== null) clearTimeout(retryTimer);
        };
    }, [enabled, sessionRevision, attempt, refreshLive, applyRealtime, setRealtimeEnabled, store, connect]);

    /** Повторяет подключение к обновлениям */
    const retry = useCallback(() => setAttempt((value) => value + 1), []);

    return {
        pinsRevision,
        connection,
        available,
        error,
        retry
    };
};

/** Общее состояние подключения к обновлениям переписки */
export const ChatDialogRealtimeContext = createContext<ReturnType<typeof useChatDialogRealtime> | null>(null);

/** Получает состояние единственной подписки переписки в админке */
export const useChatDialogRealtimeState = () => {
    const state = useContext(ChatDialogRealtimeContext);

    if (!state) throw new Error('Не найден общий провайдер обновлений переписки');

    return state;
};
